import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

const AuthContext = createContext(null);

const API_ROOT = process.env.REACT_APP_API_URL || (window.location.port === '3000'
  ? '/api'
  : `${process.env.PUBLIC_URL || ''}/api`);

const TOKEN_KEY = 'ncl_user_token';
const USER_KEY  = 'ncl_user_data';

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user,  setUser]  = useState(() => {
    try { return JSON.parse(localStorage.getItem(USER_KEY)); } catch { return null; }
  });
  const [loading, setLoading] = useState(false);

  // Re-validate token on mount
  useEffect(() => {
    if (!token) return;
    fetch(`${API_ROOT}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          setUser(data.user);
          localStorage.setItem(USER_KEY, JSON.stringify(data.user));
        } else {
          logout();
        }
      })
      .catch(() => {/* network error — keep cached user */});
  }, []); // eslint-disable-line

  const saveSession = useCallback((tok, usr) => {
    setToken(tok);
    setUser(usr);
    localStorage.setItem(TOKEN_KEY, tok);
    localStorage.setItem(USER_KEY, JSON.stringify(usr));
  }, []);

  const register = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const res  = await fetch(`${API_ROOT}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Registration failed.');
      saveSession(data.token, data.user);
      return data.user;
    } finally {
      setLoading(false);
    }
  }, [saveSession]);

  const login = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const res  = await fetch(`${API_ROOT}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Login failed.');
      saveSession(data.token, data.user);
      return data.user;
    } finally {
      setLoading(false);
    }
  }, [saveSession]);

  const updateProfile = useCallback(async (profileData) => {
    setLoading(true);
    try {
      const res  = await fetch(`${API_ROOT}/auth/profile`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(profileData),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Profile update failed.');
      setUser(data.user);
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      return data.user;
    } finally {
      setLoading(false);
    }
  }, [token]);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  }, []);

  const isProfileComplete = user && user.name && user.phone && user.address;

  return (
    <AuthContext.Provider value={{
      user, token, loading,
      isLoggedIn: !!user,
      isProfileComplete: !!isProfileComplete,
      login, register, logout, updateProfile,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
